import { useCallback, useRef } from 'react';
import {
  clampToPage, normalizedToScreen, screenToNormalized,
  type Field, type NormRect,
} from '@beckersign/shared';

interface Props {
  field: Field;
  color: string;
  selected: boolean;
  renderedW: number;
  renderedH: number;
  onSelect: (id: string, additive: boolean) => void;
  onChange: (id: string, rect: NormRect, coalesceKey: string) => void;
  onCommit: () => void;
  overlapping: boolean;
}

const HANDLE = 10;

export default function FieldBox({
  field, color, selected, renderedW, renderedH,
  onSelect, onChange, onCommit, overlapping,
}: Props) {
  const box = normalizedToScreen(field, renderedW, renderedH);
  const drag = useRef<{ mode: 'move' | 'resize'; startX: number; startY: number; orig: NormRect } | null>(null);

  const begin = useCallback((e: React.PointerEvent, mode: 'move' | 'resize') => {
    e.stopPropagation();
    e.preventDefault();
    (e.target as Element).setPointerCapture(e.pointerId);
    onSelect(field.id, e.shiftKey || e.metaKey);
    drag.current = {
      mode, startX: e.clientX, startY: e.clientY,
      orig: { x: field.x, y: field.y, width: field.width, height: field.height },
    };
  }, [field, onSelect]);

  const move = useCallback((e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) / renderedW;
    const dy = (e.clientY - d.startY) / renderedH;

    const next: NormRect = d.mode === 'move'
      ? { ...d.orig, x: d.orig.x + dx, y: d.orig.y + dy }
      : {
          ...d.orig,
          // Floor at ~6pt on a Letter page so a field cannot be resized to
          // something invisible and then lost.
          width: Math.max(0.01, d.orig.width + dx),
          height: Math.max(0.008, d.orig.height + dy),
        };

    onChange(field.id, clampToPage(next), `${d.mode}:${field.id}`);
  }, [field.id, renderedW, renderedH, onChange]);

  const end = useCallback((e: React.PointerEvent) => {
    if (!drag.current) return;
    drag.current = null;
    try { (e.target as Element).releasePointerCapture(e.pointerId); } catch { /* already released */ }
    onCommit();
  }, [onCommit]);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${field.label} field`}
      aria-pressed={selected}
      onPointerDown={(e) => begin(e, 'move')}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(field.id, false); } }}
      style={{
        position: 'absolute',
        left: box.left, top: box.top, width: box.width, height: box.height,
        border: `2px solid ${overlapping ? '#a3241c' : color}`,
        background: `${color}22`,
        borderRadius: 3,
        cursor: 'move',
        boxShadow: selected ? `0 0 0 2px #fff, 0 0 0 4px ${color}` : 'none',
        touchAction: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}
      title={overlapping ? 'Overlaps another field' : field.label}
    >
      <span style={{
        fontSize: Math.max(9, Math.min(12, box.height - 6)),
        color, fontWeight: 600, pointerEvents: 'none',
        whiteSpace: 'nowrap', padding: '0 3px',
      }}>
        {field.required ? '*' : ''}{field.label}
      </span>

      {selected && (
        <div
          onPointerDown={(e) => begin(e, 'resize')}
          onPointerMove={move}
          onPointerUp={end}
          style={{
            position: 'absolute', right: -HANDLE / 2, bottom: -HANDLE / 2,
            width: HANDLE, height: HANDLE, background: color,
            border: '1px solid #fff', borderRadius: 2, cursor: 'nwse-resize',
            touchAction: 'none',
          }}
        />
      )}
    </div>
  );
}
